import React, { useState, useEffect } from 'react'
import TopBanner from '../../../components/topBanner/topBanner'
import TitleCenter from '../../../components/title/TitleCenter'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { normalizeLang } from '../../../utils/localeDataLoader'
import { getBasePathByKeyName } from '../../../utils/navMenuCache'
import { getProductSolutionGroups } from '../../../apis/productSolutionGroups'
import { getProductSolutionCategories } from '../../../apis/productSolutionCategories'
import { productSolutionMenu as psMenuVi } from '../../../localData/PSMenu/productSolutionMenu'
import { productSolutionMenu as psMenuEn } from '../../../localData/PSMenu/productSolutionMenu_en'
import PageNotFound from '../../../components/notfound/PageNotFound'
import { FiArrowUpRight } from 'react-icons/fi'

function DetailPSCategory() {
    const { categoryID } = useParams();
    // group hiện tại (để lấy banner, title)
    const [groupData, setGroupData] = useState(null);
    // danh sách categories con của group
    const [psCategoryData, setPsCategoryData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isNotFound, setIsNotFound] = useState(false);

    const [groupId, setGroupId] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();
    const currentBasePath = location.pathname.split('/')[1];
    const { t, i18n } = useTranslation();

    useEffect(() => {
        // scroll to top on page load
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        if (!categoryID) return;

        let cancelled = false;

        const fetchData = async () => {
            setLoading(true);
            try {
                const currentLang = normalizeLang(i18n.language);

                // Fetch song song cả 2 API
                const [groups, categories] = await Promise.all([
                    getProductSolutionGroups(currentLang),
                    getProductSolutionCategories(currentLang),
                ]);

                if (!cancelled) {
                    // Tìm group tương ứng với categoryID (slug trong URL)
                    let matchedGroup = null;
                    if (Array.isArray(groups)) {
                        matchedGroup = groups.find(g => g.slug === categoryID)
                            ?? (groupId !== null ? groups.find(g => g.id === groupId) : null);
                    }

                    if (matchedGroup) {
                        if (matchedGroup.isActive === false || matchedGroup.isActive === 0) {
                            setGroupData(null);
                            setPsCategoryData([]);
                            setIsNotFound(true);
                            return;
                        }

                        const expectedBasePath = currentLang === 'vi' ? 'san-pham-giai-phap' : 'products-solutions';
                        if (matchedGroup.slug !== categoryID || currentBasePath !== expectedBasePath) {
                            navigate(`/${expectedBasePath}/${matchedGroup.slug}`, { replace: true });
                        }

                        setGroupData(matchedGroup);
                        setGroupId(matchedGroup.id);

                        // Lọc categories theo groupSlug
                        const filtered = categories.filter(c => c.groupSlug === matchedGroup.slug);
                        setPsCategoryData(filtered);
                        setIsNotFound(false);
                    } else {
                        const isEcoSlug = /phong-kham|benh-vien|dao-tao|giao-duc|truyen-thong|thuong-mai|healthcare|clinic|education|technology|pharma|investment|infrastructure/.test(categoryID);
                        if (isEcoSlug) {
                            const ecoBase = currentLang === 'vi' ? 'he-sinh-thai' : 'ecosystem';
                            navigate(`/${ecoBase}/${categoryID}`, { replace: true });
                            return;
                        }
                        setGroupData(null);
                        setPsCategoryData([]);
                        setIsNotFound(true);
                    }
                }
            } catch (error) {
                console.error('[DetailPSCategory] Lỗi khi tải dữ liệu:', error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchData();
        return () => { cancelled = true; };
    }, [categoryID, i18n.language, currentBasePath]);

    const psMenu = normalizeLang(i18n.language) === 'en' ? psMenuEn : psMenuVi;
    const data = psMenu.find(item => item.slug === categoryID) || psMenu.find(item => item.slug === groupData?.slug);

    if (!loading && isNotFound) {
        return <PageNotFound />;
    }

    return (
        <div className='DetailPSCategory'>
            <TopBanner
                titleCenter={t('home.products_title') || "Sản Phẩm và Giải Pháp"}
                imgBanner={data ? data.banner : ""}
                pageName={t('home.products_title') || "Sản Phẩm và Giải Pháp"}
                text={groupData ? groupData.name : ""}
            />
            <div className='layout_container'>
                <TitleCenter text={groupData ? groupData.name : ""} />
                <div className='PScategory'>
                    {loading ? (
                        <p style={{ textAlign: 'center', padding: '40px 0' }}>Đang tải...</p>
                    ) : (
                        <ul className='PScategory-list PScategory-list--products'>
                            {psCategoryData.map((value, index) => {
                                const targetUrl = value.productUrl || value.productLink || value.websiteUrl;
                                const handleCategoryClick = () => {
                                    if (targetUrl) {
                                        if (/^https?:\/\//i.test(targetUrl)) {
                                            window.open(targetUrl, '_blank', 'noopener,noreferrer');
                                        } else {
                                            navigate(targetUrl);
                                        }
                                    } else {
                                        const basePath = getBasePathByKeyName('menu.products', normalizeLang(i18n.language)) || currentBasePath;
                                        navigate(`/${basePath}/${categoryID}/${value.slug}`);
                                    }
                                };
                                return (
                                    <li
                                        key={value.id || index}
                                        style={{ "--ps-delay": `${index * 80}ms` }}
                                        onClick={handleCategoryClick}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                handleCategoryClick();
                                            }
                                        }}
                                        className="PScategory-item"
                                        role="link"
                                        tabIndex={0}
                                    >
                                        <span className="PScategory-item__index" aria-hidden="true">
                                            {String(index + 1).padStart(2, '0')}
                                        </span>
                                        <span className="PScategory-item__media">
                                            <img src={value.image} alt={value.name} />
                                        </span>
                                        <div className="PScategory-item__content">
                                            <span className="PScategory-item__badge">{groupData?.name || "GIẢI PHÁP Y TẾ"}</span>
                                            <h4>{value.name}</h4>
                                            {value.description && (
                                                <p className="PScategory-item__desc">
                                                    {value.description.length > 100 
                                                        ? `${value.description.substring(0, 100)}...` 
                                                        : value.description}
                                                </p>
                                            )}
                                        </div>
                                        <span className="PScategory-item__footer">
                                            <span className="PScategory-item__link-text">Xem chi tiết</span>
                                            <span className="PScategory-item__arrow" aria-hidden="true">
                                                <FiArrowUpRight />
                                            </span>
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DetailPSCategory
